import {
  type ThemeOptions as MuiThemeOptions,
  ThemeProvider as MuiThemeProvider,
  unstable_createMuiStrictModeTheme as createMuiTheme
} from "@material-ui/core"
import {
  ThemeProvider as PixelThemeProvider,
  createTheme as createPixelTheme,
  getThemeOptions as getPixelThemeOptions,
  seSepSeTokens
} from "@infinitaslearning/pixel-design-system"
import type { IReactAutoPollOptions } from "configcat-react"
import {
  ConfigCatProvider,
  OverrideBehaviour,
  PollingMode,
  createConsoleLogger,
  createFlagOverridesFromMap
} from "configcat-react"
import type { RouterLocation } from "connected-react-router"
import { ConnectedRouter } from "connected-react-router"
import { createRef, useEffect, useMemo, useState } from "react"
import type { RefObject } from "react"
import { connect } from "react-redux"
import { config } from "../config"
import { useFullBrandName, useOpcoTheme } from "../hooks"
import "../i18n/i18nInit"
import { errorInterceptor } from "../interceptors/error.interceptor"
import { Opco } from "../models"
import type { AppState, AppThunkDispatch, ProfileAction } from "../redux"
import { fetchProfile, history } from "../redux"
import { httpClient } from "../services"
import { ScrollReset } from "../utils"
import { IdentityProvider } from "./indentity-provider.component"
import { PageFooter, PageHeader, SnackbarProvider } from "./molecules"
import { Routing } from "./routing.component"
import { TelemetryProviderWrapper } from "./telemetry-provider.component"

import "./App.scss"
import { useTracker } from "../hooks/useTracker"
import { i18nInit } from "../i18n/i18nInit"

export interface IAppProps {
  location: RouterLocation<unknown>
  fetchProfile: () => void
}

declare global {
  interface Window {
    Buffer: any
  }
}

window.Buffer = window.Buffer || require("buffer").Buffer

function AppView(props: IAppProps) {
  const { location, fetchProfile } = props
  useTracker()
  const opcoThemeOptions: MuiThemeOptions = useOpcoTheme()
  const hideFooterPages: string[] = ["/map", "/project", "/comparison"]
  const fullBrandName: string = useFullBrandName()
  const pageWrapperRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>()

  const hideFooter: boolean = useMemo<boolean>(() => {
    for (const page of hideFooterPages) {
      if (location.pathname.startsWith(page)) {
        return true
      }
    }
    return false
  }, [location])

  const [i18nLoaded, seti18nLoaded] = useState(false)

  const handleSetInitFinished = () => {
    seti18nLoaded(true)
    return undefined
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    i18nInit(handleSetInitFinished)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    errorInterceptor(httpClient)
    setTheme()
    fetchProfile()
    window.addEventListener(ScrollReset, scrollTop)

    return () => {
      window.removeEventListener(ScrollReset, scrollTop)
    }
  }, [])

  useEffect(() => {
    document.title = fullBrandName
  }, [fullBrandName])

  // Mui theme will be used to keep the original bosatlas look
  const muiTheme = createMuiTheme(opcoThemeOptions)
  // Pixel theme will be used by the auth-client
  const pixelTheme = createPixelTheme(getPixelThemeOptions(seSepSeTokens))

  const configCatOptions: IReactAutoPollOptions = {
    pollIntervalSeconds: 300,
    requestTimeoutMs: 5000,
    maxInitWaitTimeSeconds: 0.5,
    logger: createConsoleLogger(-1),
    flagOverrides: createFlagOverridesFromMap({}, OverrideBehaviour.LocalOverRemote)
  }

  function setTheme(): void {
    if (window.location.href.includes("plantyn.com"))
      window.localStorage.setItem(config.selectedOpcoLocalstorageKey, `${Opco.Plantyn}`)
    else if (window.location.href.includes("liber.se"))
      window.localStorage.setItem(config.selectedOpcoLocalstorageKey, `${Opco.Liber}`)
    else window.localStorage.setItem(config.selectedOpcoLocalstorageKey, `${Opco.Noordhoff}`)
  }

  function scrollTop(): void {
    const element: HTMLDivElement | null = pageWrapperRef.current
    if (!element) return

    element.scrollTo({ top: 0, behavior: "smooth" })
  }

  if (!i18nLoaded) return null

  return (
    <div id="app" className="App">
      <MuiThemeProvider theme={muiTheme}>
        <PixelThemeProvider theme={pixelTheme}>
          <TelemetryProviderWrapper>
            <IdentityProvider>
              <ConfigCatProvider
                sdkKey={config.configcat.featuresKey}
                pollingMode={PollingMode.AutoPoll}
                options={configCatOptions}
              >
                <SnackbarProvider>
                  <ConnectedRouter history={history}>
                    <PageHeader />
                    <div ref={pageWrapperRef} className="page-wrapper">
                      <Routing />
                    </div>
                    {!hideFooter && <PageFooter />}
                  </ConnectedRouter>
                </SnackbarProvider>
              </ConfigCatProvider>
            </IdentityProvider>
          </TelemetryProviderWrapper>
        </PixelThemeProvider>
      </MuiThemeProvider>
    </div>
  )
}

function mapStateToProps(state: AppState) {
  return {
    location: state.router.location
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ProfileAction>) {
  return {
    fetchProfile: () => dispatch(fetchProfile())
  }
}

export const App = connect(mapStateToProps, mapDispatchToProps)(AppView)
